import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { getUsers } from '@/api/users';
import { addPermissionToUser, getPermissions, getPermissionsForUser, removePermissionFromUser } from '@/api/permissions';
import { getServices } from '@/api/services';

interface User {
  id: string;
  email: string;
  permissions: Permission[];
}

interface Permission {
  id: string;
  name: string;
  serviceId: string;
}

interface Service {
  id: string;
  name: string;
}

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchUsers(),
          fetchPermissions(),
          fetchServices()
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersData = await getUsers();
      const usersWithPermissions = await Promise.all(
        usersData?.map(async (user: User) => {
          const userPermissions = await fetchUserPermissions(user.id);
          return {
            ...user,
            permissions: userPermissions
          };
        })
      );
      setUsers(usersWithPermissions);
    } catch (error) {
      console.error(`Error in fetchUsers: ${error}`);
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    }
  };

  const fetchPermissions = async () => {
    try {
      const data = await getPermissions();
      setPermissions(data);
    } catch (error) {
      console.error(`Error in fetchPermissions: ${error}`);
      toast({
        title: "Error",
        description: "Failed to fetch permissions",
        variant: "destructive",
      });
    }
  };

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      console.error(`Error in fetchServices: ${error}`);
      toast({
        title: "Error",
        description: "Failed to fetch services",
        variant: "destructive",
      });
    }
  };

  const handleAddPermission = async (userId: string, permissionId: string) => {
    try {
      await addPermissionToUser(userId, permissionId);
      fetchUsers();
      toast({
        title: "Success",
        description: "Permission added successfully",
      });
    } catch (error) {
      console.error(`Error in handleAddPermission: ${error}`);
      toast({
        title: "Error",
        description: "Failed to add permission",
        variant: "destructive",
      });
    }
  };

  const handleRemovePermission = async (userId: string, permissionId: string) => {
    try {
      await removePermissionFromUser(userId, permissionId);
      fetchUsers();
      toast({
        title: "Success",
        description: "Permission removed successfully",
      });
    } catch (error) {
      console.error(`Error in handleRemovePermission: ${error}`);
      toast({
        title: "Error",
        description: "Failed to remove permission",
        variant: "destructive",
      });
    }
  };

  const fetchUserPermissions = async (userId: string) => {
    try {
      const data = await getPermissionsForUser(userId);
      return data;
    } catch (error) {
      console.error(`Error in fetchUserPermissions: ${error}`);
      toast({
        title: "Error",
        description: "Failed to fetch user permissions",
        variant: "destructive",
      });
      return [];
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Users</h1>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Permissions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={2} className="text-center">
                Loading users and permissions...
              </TableCell>
            </TableRow>
          ) : users?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2} className="text-center">
                No users found
              </TableCell>
            </TableRow>
          ) : (
            users?.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {user.permissions?.length === 0 ? (
                        <div className="text-sm text-muted-foreground">
                          No permissions assigned
                        </div>
                      ) : (
                        user.permissions?.map((permission) => (
                          <div 
                            key={permission.id} 
                            className="flex items-center gap-2 bg-secondary/50 px-3 py-1 rounded-md"
                          >
                            <span className="font-medium">
                              {permission.name}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              ({services.find((s) => s.id === permission.serviceId)?.name})
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                              onClick={() => handleRemovePermission(user.id, permission.id)}
                            >
                              ×
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                    <Select onValueChange={(value) => handleAddPermission(user.id, value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Add permission" />
                      </SelectTrigger>
                      <SelectContent>
                        {permissions
                          ?.filter((p) => !user.permissions?.some((up) => up.id === p.id))
                          .map((permission) => (
                            <SelectItem key={permission.id} value={permission.id}>
                              {permission.name} ({services?.find((s) => s.id === permission.serviceId)?.name})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default Users; 
